import {
  Button,
  Link,
  Modal,
  Notification,
  notifySuccess,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useApproveInvoiceReportMutation } from '@omnipresentgroup/domains';
import type { InvoiceReport } from '@omnipresentgroup/types';
import { useEffect } from 'react';

import { useManagerUser } from '../../../../hooks';
import { WrappedMixpanel } from '../../../../mixpanelWrapper';

const SUPPORT_LINK =
  'https://help.omnipresent.com/en/articles/7891775-contact-us-the-support-team';

export const InvoiceReportApproveModal = ({
  closeFn,
  open,
  invoice,
}: {
  closeFn: () => void;
  open: boolean;
  invoice: InvoiceReport;
}) => {
  const { userId, companyId } = useManagerUser();

  const {
    mutate: approveInvoiceReport,
    isSuccess,
    isPending,
  } = useApproveInvoiceReportMutation();

  useEffect(() => {
    if (isSuccess) {
      notifySuccess({
        title: 'The invoice has been successfully approved.',
      });
      closeFn();
    }
  }, [closeFn, isSuccess]);

  const noInvoicingEmails =
    !invoice?.invoicingEmails || invoice.invoicingEmails.length === 0;

  return (
    <Modal
      open={open}
      span={3}
      onOpenChange={() => {
        closeFn();
      }}
    >
      <Modal.Header title="Approve invoice?" description={invoice.id} />

      <Modal.Body>
        <Stack justify="space-between" gap="12">
          {noInvoicingEmails ? (
            <Notification
              id="no-invoicing-email-notification"
              title="It is not possible to approve the invoice as no invoicing email
              address has been set up."
              intent="warning"
            />
          ) : (
            <>
              <Typography as="span" size="14">
                Are you sure you wish to approve this invoice?
              </Typography>
              <Typography as="span" size="14">
                We’ll email{' '}
                <Typography as="span" size="14" weight="medium">
                  {invoice?.invoicingEmails.join(', ')}
                </Typography>
              </Typography>
            </>
          )}
          <Typography as="span" size="14">
            To {noInvoicingEmails ? 'add' : 'change'} your invoicing email
            addresses contact our support team via live chat or{' '}
            <Typography as="span" size="14" weight="medium">
              help@omnipresent.com.
            </Typography>{' '}
            Here's an{' '}
            <Link size="14" to={SUPPORT_LINK} target="_blank">
              article
            </Link>{' '}
            explaining how to do that.
          </Typography>
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={isPending}
          onClick={() => {
            WrappedMixpanel.track('Approve Invoice Cancelled', {
              companyId,
              userId,
              invoiceId: invoice.id,
              memo: invoice.reference,
            });
            closeFn();
          }}
        >
          No
        </Button>
        <Button
          loading={isPending}
          disabled={!invoice || noInvoicingEmails}
          onClick={() => {
            WrappedMixpanel.track('Approve Invoice Confirmed', {
              companyId,
              userId,
              invoiceId: invoice.id,
              memo: invoice.reference,
            });
            approveInvoiceReport({ companyId, invoiceId: invoice.id });
          }}
        >
          Yes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
